import { Link } from "react-router-dom";
import { useState } from "react";
import FrameOverlay from "@/components/base/FrameOverlay";
import TiltCard from "@/components/base/TiltCard";
import { useInView } from "@/hooks/useInView";

interface Card {
  src: string;
  caption: string;
}

const cards: Card[] = [
  {
    src: "https://readdy.ai/api/search-image?query=Elegant%20female%20tattoo%20artist%20in%20her%20thirties%20dark%20hair%20tied%20back%20cream%20linen%20shirt%20marble%20walled%20studio%20warm%20golden%20lighting%20classical%20framed%20artwork%20renaissance%20gallery%20soft%20amber%20glow%20editorial%20portrait%20photography&width=900&height=1100&seq=ap2-card-001&orientation=portrait",
    caption: "Isabella — Portrait",
  },
  {
    src: "https://readdy.ai/api/search-image?query=Sacred%20geometry%20sleeve%20tattoo%20with%20Egyptian%20hieroglyphic%20motifs%20delicate%20gold%20accent%20ink%20fine%20line%20detail%20warm%20amber%20tones%20classical%20ancient%20art%20aesthetic%20professional%20editorial%20photography&width=900&height=1100&seq=ap2-card-002&orientation=portrait",
    caption: "Sacred Geometry Sleeve",
  },
  {
    src: "https://readdy.ai/api/search-image?query=Ornamental%20back%20piece%20tattoo%20with%20Byzantine%20mosaic%20inspired%20patterns%20sacred%20geometry%20radiating%20design%20warm%20bronze%20and%20gold%20tones%20intricate%20fine%20line%20work%20professional%20photography%20museum%20quality&width=900&height=1100&seq=ap2-card-003&orientation=portrait",
    caption: "Byzantine Back Piece",
  },
  {
    src: "https://readdy.ai/api/search-image?query=Mandala%20geometric%20chest%20tattoo%20with%20Egyptian%20ankh%20and%20eye%20of%20horus%20motifs%20delicate%20line%20work%20subtle%20gold%20accents%20warm%20amber%20skin%20tones%20professional%20editorial%20photography&width=900&height=1100&seq=ap2-card-004&orientation=portrait",
    caption: "Egyptian Mandala",
  },
  {
    src: "https://readdy.ai/api/search-image?query=Forearm%20tattoo%20with%20Egyptian%20winged%20scarab%20and%20hieroglyphic%20band%20delicate%20gold%20accent%20ink%20fine%20ornamental%20detail%20warm%20amber%20tones%20professional%20macro%20tattoo%20photography&width=900&height=1100&seq=ap2-card-005&orientation=portrait",
    caption: "Scarab Glyph",
  },
];

export default function ArtistProfile2() {
  const [top, setTop] = useState(0);
  const [doorOpen, setDoorOpen] = useState(false);
  const next = () => setTop((p) => (p + 1) % cards.length);
  const prev = () => setTop((p) => (p - 1 + cards.length) % cards.length);

  const handleEnter = () => {
    // navigate immediately via anchor click so the global shatter transition handles it
    const link = document.createElement("a");
    link.href = "/artist/isabella-rossi";
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  return (
    <section className="bg-foreground-950 text-background-50 py-24 md:py-36 border-t border-foreground-950/10 overflow-hidden relative">
      {/* Ambient glow */}
      <div className="absolute top-1/3 right-1/4 w-[500px] h-[500px] rounded-full opacity-[0.05] pointer-events-none animate-halo"
        style={{ background: "radial-gradient(circle, oklch(var(--accent-500)), transparent 70%)" }} />

      {/* Hieroglyphic door overlay */}
      {doorOpen && (
        <div className="fixed inset-0 z-[100] pointer-events-none">
          <div className="absolute inset-y-0 left-0 w-1/2 animate-door-left fa-door-panel overflow-hidden">
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 opacity-70">
              {[...Array(6)].map((_, r) => (
                <div key={r} className="flex gap-6 animate-hiero-glow" style={{ animationDelay: `${r * 0.3}s` }}>
                  {[...Array(4)].map((_, c) => (
                    <span key={c} className="font-display text-4xl md:text-6xl" style={{ animationDelay: `${r * 0.3 + c * 0.1}s` , color: "#B5722E" }}>
                      {["𓂀", "𓋹", "𓆣", "𓅓"][c]}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div className="absolute inset-y-0 right-0 w-1/2 animate-door-right fa-door-panel overflow-hidden">
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 opacity-70">
              {[...Array(6)].map((_, r) => (
                <div key={r} className="flex gap-6 animate-hiero-glow" style={{ animationDelay: `${r * 0.3 + 0.15}s` }}>
                  {[...Array(4)].map((_, c) => (
                    <span key={c} className="font-display text-4xl md:text-6xl" style={{ animationDelay: `${r * 0.3 + c * 0.1 + 0.15}s` , color: "#B5722E" }}>
                      {["𓃭", "𓁈", "𓄿", "𓃒"][c]}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-2 h-full bg-gradient-to-b from-transparent via-[#B5722E]/70 to-transparent animate-hiero-glow" />
          </div>
        </div>
      )}

      <div className="px-6 md:px-12 max-w-7xl mx-auto">
        <div className="grid grid-cols-12 gap-8 md:gap-12 items-center">
          {/* Text side */}
          <div className="col-span-12 md:col-span-5">
            <p className="font-display text-[11px] uppercase tracking-[0.28em] text-background-50/50 mb-5">
              ( 02 ) — Sacred Geometry & Ornamental
            </p>
            <h3 className="font-display font-black leading-[0.9] tracking-[-0.04em] text-5xl md:text-6xl lg:text-7xl mb-6 md:mb-8">
              Isabella<em className="font-serif italic font-light"> Rossi</em>
            </h3>
            <p className="text-base md:text-lg text-background-50/70 leading-relaxed font-serif mb-10">
              Drawing from ancient Egyptian temple art, sacred geometry, and Byzantine mosaics — Isabella treats the body as architecture. Each click peels another layer of her portfolio.
            </p>

            <div className="flex items-center gap-6 mb-10">
              <div className="font-display text-xs uppercase tracking-[0.3em] text-background-50/60">
                <span className="text-background-50 text-base font-black">{String(top + 1).padStart(2, "0")}</span>
                <span className="mx-2">/</span>
                {String(cards.length).padStart(2, "0")}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={prev}
                  className="w-10 h-10 rounded-full border border-background-50/30 flex items-center justify-center text-background-50 hover:bg-background-50 hover:text-foreground-950 transition-colors cursor-pointer"
                  aria-label="Previous"
                >
                  <i className="ri-arrow-left-line text-base"></i>
                </button>
                <button
                  onClick={next}
                  className="w-10 h-10 rounded-full border border-background-50/30 flex items-center justify-center text-background-50 hover:bg-background-50 hover:text-foreground-950 transition-colors cursor-pointer"
                  aria-label="Next"
                >
                  <i className="ri-arrow-right-line text-base"></i>
                </button>
              </div>
            </div>

            <p className="font-serif italic text-lg text-background-50/80 mb-8">
              &ldquo;{cards[top].caption}&rdquo;
            </p>

            <button
              onClick={handleEnter}
              className="group inline-flex items-center gap-3 cursor-pointer"
            >
              <span className="font-display text-xs uppercase tracking-[0.25em] text-background-50 border-b border-background-50/40 pb-1 group-hover:border-background-50 transition-colors whitespace-nowrap">
                Enter the Gallery
              </span>
              <span className="w-9 h-9 rounded-full border border-background-50/30 flex items-center justify-center text-background-50 transition-all duration-500 group-hover:bg-background-50 group-hover:text-foreground-950">
                <i className="ri-arrow-right-up-line text-sm"></i>
              </span>
            </button>
          </div>

          {/* Card stack side */}
          <div className="col-span-12 md:col-span-7 flex justify-center items-center" data-product-shop>
            <TiltCard className="w-full max-w-md cursor-pointer" maxTilt={8} scale={1.02}>
              <div
                style={{ aspectRatio: "3 / 4" }}
                onClick={next}
              >
              {cards.map((c, i) => {
                const offset = (i - top + cards.length) % cards.length;
                const isTop = offset === 0;
                const rotations = [0, -3, 5, -6, 4];
                const translates = [0, 14, 28, 42, 56];
                const rot = rotations[offset % rotations.length];
                const tx = translates[offset] || 0;
                return (
                  <FadingCard
                    key={i}
                    card={c}
                    index={i}
                    isTop={isTop}
                    offset={offset}
                    rotation={rot}
                    translateX={tx}
                    totalCards={cards.length}
                  />
                );
              })}
              <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 font-display text-[10px] uppercase tracking-[0.3em] text-background-50/50 whitespace-nowrap">
                Tap to explore
              </div>
            </div>
            </TiltCard>
          </div>
        </div>
      </div>
    </section>
  );
}

function FadingCard({
  card,
  index,
  isTop,
  offset,
  rotation,
  translateX,
  totalCards,
}: {
  card: Card;
  index: number;
  isTop: boolean;
  offset: number;
  rotation: number;
  translateX: number;
  totalCards: number;
}) {
  const { ref, isInView } = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <div
      ref={ref}
      className="absolute inset-0 transition-all duration-700 ease-out"
      style={{
        transform: `translate(${translateX * 0.6}px, ${translateX * 0.4}px) rotate(${rotation}deg)`,
        zIndex: totalCards - offset,
        opacity: isInView ? (isTop ? 1 : 0.55 - offset * 0.08) : 0,
        filter: isTop ? "none" : `blur(${offset}px) saturate(0.7)`,
        transitionDelay: `${index * 150}ms`,
      }}
    >
      <div className="w-full h-full overflow-hidden bg-background-100 relative">
        <img
          src={card.src}
          alt={card.caption}
          draggable={false}
          className="w-full h-full object-cover object-top"
        />
        <FrameOverlay />
      </div>
    </div>
  );
}